import React, {
    KeyboardEvent,
    MouseEvent,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Button, Input, Spin } from "antd";
import {
    ArrowRightOutlined,
    SearchOutlined,
    RightOutlined,
} from "@ant-design/icons";
import { WbSubject } from "../../workbench/types/WbTypes";
import WorkbenchNewEntityTypeTag from "./WorkbenchNewEntityTypeTag";
import WorkbenchNewHighlight from "./WorkbenchNewHighlight";

type Props = {
    error: string | null;
    isLoading: boolean;
    onSearchModeChange: (active: boolean) => void;
    onSearch: () => void;
    onSelect: (subject: WbSubject) => void;
    query: string;
    setQuery: (value: string) => void;
    suggestions: WbSubject[];
    totalCount: number;
};

type SuggestionViewModel = {
    subject: WbSubject;
    title: string;
    identifier: string;
    primaryReference: string;
    party: string;
    period: string;
    status: string;
    matchReason: string;
    matchValue: string;
    rank: number;
};

const MAX_VISIBLE_SUGGESTIONS = 10;
const AUTOCOMPLETE_PANEL_TOP_OFFSET = 76;
const AUTOCOMPLETE_PANEL_BOUNDARY_GAP = 12;

const normalizeIdentifier = (value: string) =>
    value.replace(/[^a-z0-9]/gi, "").toLowerCase();

const tokenize = (value: string) =>
    value.trim().toLowerCase().split(/\s+/).filter(Boolean);

const formatDateYear = (value?: string | null) => {
    if (!value) {
        return "";
    }

    const year = new Date(value).getFullYear();
    return Number.isNaN(year) ? "" : String(year);
};

const getSubjectParty = (subject: WbSubject) =>
    subject.cedent ??
    subject.context?.cedent ??
    subject.context?.claimant ??
    subject.context?.broker ??
    "";

const getSubjectPeriod = (subject: WbSubject) => {
    if (subject.kind === "Claim" && subject.context?.dateOfLoss) {
        return `Loss date ${subject.context.dateOfLoss}`;
    }

    if (subject.context?.uwYear) {
        return `UW Year ${subject.context.uwYear}`;
    }

    const periodYears = [
        formatDateYear(subject.periodFrom),
        formatDateYear(subject.periodTo),
    ].filter(Boolean);

    return Array.from(new Set(periodYears)).join("-");
};

const getSubjectTitle = (subject: WbSubject) => {
    if (subject.search?.title) {
        return subject.search.title;
    }

    const party = getSubjectParty(subject);
    const mainClass = subject.context?.mainClass?.trim();
    const year = subject.context?.uwYear;
    const base = party || subject.identifier;

    if (mainClass && year) {
        return `${base} ${mainClass} ${year}`;
    }

    if (mainClass) {
        return `${base} ${mainClass}`;
    }

    return base;
};

const getSearchPrimaryReference = (subject: WbSubject) =>
    subject.search?.primaryReference || subject.identifier;

const getSearchParty = (subject: WbSubject) =>
    subject.search?.party || getSubjectParty(subject);

const getSearchPeriod = (subject: WbSubject) =>
    subject.search?.period || getSubjectPeriod(subject);

const getStatusLabel = (subject: WbSubject) => {
    if (subject.search?.status) {
        return subject.search.status;
    }

    if (subject.state.overallStatus === "InProgress") {
        return "In Progress";
    }

    if (subject.state.overallStatus === "OnHold") {
        return "On Hold";
    }

    return subject.state.overallStatus;
};

const getMatchReason = (subject: WbSubject) => subject.search?.matchLabel ?? "";

const getMatchValue = (subject: WbSubject) =>
    subject.search?.matchValue || subject.search?.matchedTaskAlphaId || "";

const getRank = (
    subject: WbSubject,
    query: string,
    title: string,
    party: string,
    primaryReference: string,
) => {
    if (typeof subject.search?.rank === "number") {
        return subject.search.rank;
    }

    const cleanQuery = query.trim();
    const normalizedQuery = normalizeIdentifier(cleanQuery);
    const normalizedIdentifier = normalizeIdentifier(primaryReference);

    if (normalizedQuery && normalizedIdentifier === normalizedQuery) {
        return 0;
    }

    const lowerQuery = cleanQuery.toLowerCase();
    const tokens = tokenize(cleanQuery);
    const businessReference = [
        subject.identifier,
        primaryReference,
        title,
        party,
        subject.context?.broker ?? "",
        subject.context?.claimant ?? "",
        subject.context?.cedent ?? "",
    ]
        .join(" ")
        .toLowerCase();

    if (
        lowerQuery &&
        (subject.identifier.toLowerCase().startsWith(lowerQuery) ||
            title.toLowerCase().startsWith(lowerQuery) ||
            party.toLowerCase().startsWith(lowerQuery) ||
            tokens.every((token) => businessReference.includes(token)))
    ) {
        return 1;
    }

    return 2;
};

const buildSuggestionViewModels = (
    suggestions: WbSubject[],
    query: string,
): SuggestionViewModel[] => {
    const deduplicated = new Map<string, WbSubject>();

    suggestions.forEach((subject) => {
        const key = subject.id || `${subject.kind}:${subject.identifier}`;

        if (!deduplicated.has(key)) {
            deduplicated.set(key, subject);
        }
    });

    return Array.from(deduplicated.values())
        .map((subject) => {
            const title = getSubjectTitle(subject);
            const party = getSearchParty(subject);
            const primaryReference = getSearchPrimaryReference(subject);

            return {
                subject,
                title,
                identifier: subject.identifier,
                primaryReference,
                party,
                period: getSearchPeriod(subject),
                status: getStatusLabel(subject),
                matchReason: getMatchReason(subject),
                matchValue: getMatchValue(subject),
                rank: getRank(subject, query, title, party, primaryReference),
            };
        })
        .sort(
            (left, right) =>
                left.rank - right.rank ||
                left.primaryReference.localeCompare(right.primaryReference),
        );
};

const WorkbenchNewSearch: React.FC<Props> = ({
    error,
    isLoading,
    onSearchModeChange,
    onSearch,
    onSelect,
    query,
    setQuery,
    suggestions,
    totalCount,
}) => {
    const trimmedQuery = query.trim();
    const searchRef = useRef<HTMLDivElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [previousQuery, setPreviousQuery] = useState(query);

    const rankedSuggestions = useMemo(
        () => buildSuggestionViewModels(suggestions, query),
        [query, suggestions],
    );
    const visibleSuggestions = rankedSuggestions.slice(
        0,
        MAX_VISIBLE_SUGGESTIONS,
    );
    const visibleCount = Math.min(
        totalCount || rankedSuggestions.length,
        MAX_VISIBLE_SUGGESTIONS,
    );
    const totalResults = totalCount || rankedSuggestions.length;
    const showSearchInteraction = trimmedQuery.length >= 2;
    const showPanel = isOpen && showSearchInteraction;
    const showFooter = !isLoading && !error && visibleSuggestions.length > 0;
    const exactIdentifierMatch =
        rankedSuggestions.find((suggestion) =>
            [
                suggestion.identifier,
                suggestion.primaryReference,
                suggestion.matchValue,
            ].some(
                (value) =>
                    value &&
                    normalizeIdentifier(value) ===
                        normalizeIdentifier(trimmedQuery),
            ),
        ) ?? null;
    const activeSuggestion =
        activeIndex >= 0 ? visibleSuggestions[activeIndex] : null;
    const activeOptionId = activeSuggestion
        ? `workbench-new-suggestion-${activeIndex}`
        : undefined;

    useEffect(() => {
        const queryChanged = previousQuery !== query;
        setPreviousQuery(query);
        setActiveIndex(-1);

        if (!showSearchInteraction) {
            setIsOpen(false);
            return;
        }

        if (queryChanged) {
            setIsOpen(true);
        }
    }, [previousQuery, query, showSearchInteraction]);

    useEffect(() => {
        onSearchModeChange(showPanel);
    }, [onSearchModeChange, showPanel]);

    useLayoutEffect(() => {
        const searchElement = searchRef.current;

        if (!showPanel || !searchElement) {
            return;
        }

        const setAvailablePanelHeight = () => {
            const searchTop = searchElement.getBoundingClientRect().top;
            const contentBoundary =
                searchElement
                    .closest<HTMLElement>(".ant-layout-content")
                    ?.getBoundingClientRect().bottom ?? window.innerHeight;
            const availableHeight =
                contentBoundary -
                searchTop -
                AUTOCOMPLETE_PANEL_TOP_OFFSET -
                AUTOCOMPLETE_PANEL_BOUNDARY_GAP;

            searchElement.style.setProperty(
                "--workbench-new-autocomplete-max-height",
                `${Math.max(180, availableHeight)}px`,
            );
        };

        setAvailablePanelHeight();
        window.addEventListener("resize", setAvailablePanelHeight);

        return () => {
            window.removeEventListener("resize", setAvailablePanelHeight);
            searchElement.style.removeProperty(
                "--workbench-new-autocomplete-max-height",
            );
        };
    }, [showPanel]);

    useEffect(() => {
        if (!showPanel || !activeOptionId) {
            return;
        }

        const activeOption = document.getElementById(activeOptionId);

        activeOption?.scrollIntoView?.({ block: "nearest" });
    }, [activeOptionId, showPanel]);

    const openSubject = (subject: WbSubject) => {
        setIsOpen(false);
        setActiveIndex(-1);
        onSelect(subject);
    };

    const openCurrentSubject = () => {
        if (activeSuggestion) {
            openSubject(activeSuggestion.subject);
            return;
        }

        if (exactIdentifierMatch) {
            openSubject(exactIdentifierMatch.subject);
            return;
        }

        if (rankedSuggestions.length === 1) {
            openSubject(rankedSuggestions[0].subject);
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Escape") {
            setIsOpen(false);
            setActiveIndex(-1);
            return;
        }

        if (!showSearchInteraction) {
            return;
        }

        if (event.key === "ArrowDown" && visibleSuggestions.length > 0) {
            event.preventDefault();
            setIsOpen(true);
            setActiveIndex((index) =>
                index < 0 ? 0 : (index + 1) % visibleSuggestions.length,
            );
            return;
        }

        if (event.key === "ArrowUp" && visibleSuggestions.length > 0) {
            event.preventDefault();
            setIsOpen(true);
            setActiveIndex((index) =>
                index <= 0 ? visibleSuggestions.length - 1 : index - 1,
            );
            return;
        }

        if (event.key === "Enter" && showSearchInteraction) {
            event.preventDefault();

            if (activeSuggestion) {
                openSubject(activeSuggestion.subject);
                return;
            }

            setIsOpen(true);
            setActiveIndex(-1);
            onSearch();
        }
    };

    const handlePointerSelect = (
        event: MouseEvent<HTMLElement>,
        suggestion: SuggestionViewModel,
    ) => {
        event.preventDefault();
        openSubject(suggestion.subject);
    };

    const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(event.target.value);
    };

    const renderFooter = () => {
        if (!showFooter) {
            return null;
        }

        return (
            <div className="autocomplete-footer workbench-new-search-footer">
                <span>
                    {totalResults > MAX_VISIBLE_SUGGESTIONS
                        ? `${totalResults} entities found · Refine your search`
                        : `${visibleCount} suggested ${
                              visibleCount === 1 ? "entity" : "entities"
                          }`}
                </span>
                <span>Press Enter to search</span>
            </div>
        );
    };

    const renderPanelContent = () => {
        if (isLoading) {
            return (
                <div
                    className="autocomplete-state autocomplete-loading"
                    role="status"
                >
                    <Spin size="small" />
                    <span>Searching entities...</span>
                </div>
            );
        }

        if (error) {
            return (
                <div
                    className="autocomplete-state autocomplete-error"
                    role="alert"
                >
                    <strong>We could not complete the search.</strong>
                    <span>Check your connection or try again in a moment.</span>
                </div>
            );
        }

        if (visibleSuggestions.length === 0) {
            return (
                <div
                    className="autocomplete-state autocomplete-empty workbench-new-autocomplete-empty"
                    role="status"
                >
                    <strong>
                        No entities found for &ldquo;{trimmedQuery}&rdquo;
                    </strong>
                    <span>
                        Try an identifier, party name, year, or another known
                        detail.
                    </span>
                </div>
            );
        }

        return (
            <div className="workbench-new-search-options">
                {visibleSuggestions.map((suggestion, index) => (
                    <Button
                        aria-selected={activeIndex === index}
                        className={`autocomplete-option workbench-new-search-option${
                            activeIndex === index ? " active" : ""
                        }`}
                        htmlType="button"
                        id={`workbench-new-suggestion-${index}`}
                        key={suggestion.subject.id}
                        onClick={(event) =>
                            handlePointerSelect(event, suggestion)
                        }
                        onMouseMove={() => setActiveIndex(index)}
                        role="option"
                        type="text"
                    >
                        <span className="workbench-new-search-option-kind">
                            <WorkbenchNewEntityTypeTag
                                kind={suggestion.subject.kind}
                            />
                        </span>
                        <span className="workbench-new-search-option-copy">
                            <span className="workbench-new-search-option-title">
                                <WorkbenchNewHighlight
                                    query={query}
                                    text={suggestion.title}
                                />
                            </span>
                            <small>
                                <WorkbenchNewHighlight
                                    query={query}
                                    text={suggestion.primaryReference}
                                />
                                {suggestion.primaryReference !==
                                    suggestion.identifier && (
                                    <>
                                        <span className="workbench-new-dot">
                                            .
                                        </span>
                                        <WorkbenchNewHighlight
                                            query={query}
                                            text={suggestion.identifier}
                                        />
                                    </>
                                )}
                                {suggestion.party && (
                                    <>
                                        <span className="workbench-new-dot">
                                            .
                                        </span>
                                        <WorkbenchNewHighlight
                                            query={query}
                                            text={suggestion.party}
                                        />
                                    </>
                                )}
                                {suggestion.period && (
                                    <>
                                        <span className="workbench-new-dot">
                                            .
                                        </span>
                                        {suggestion.period}
                                    </>
                                )}
                                {suggestion.status && (
                                    <>
                                        <span className="workbench-new-dot">
                                            .
                                        </span>
                                        {suggestion.status}
                                    </>
                                )}
                            </small>
                            {suggestion.matchReason && (
                                <small className="workbench-new-search-option-match">
                                    {suggestion.matchReason}
                                    {suggestion.matchValue && (
                                        <>
                                            {": "}
                                            <WorkbenchNewHighlight
                                                query={query}
                                                text={suggestion.matchValue}
                                            />
                                        </>
                                    )}
                                </small>
                            )}
                        </span>
                        <RightOutlined className="workbench-new-search-option-chevron" />
                    </Button>
                ))}
            </div>
        );
    };

    return (
        <div className="workbench-new-search" ref={searchRef}>
            <div className="workbench-new-autocomplete">
                <Input
                    allowClear
                    aria-activedescendant={activeOptionId}
                    aria-autocomplete="list"
                    aria-controls="entity-suggestions"
                    aria-expanded={showPanel}
                    aria-label="Search Workbench entities"
                    onChange={handleQueryChange}
                    onFocus={() => {
                        if (showSearchInteraction) {
                            setIsOpen(true);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="Search by ID, task, party, reference, class or participation..."
                    prefix={<SearchOutlined />}
                    role="combobox"
                    suffix={
                        <Button
                            aria-label="Open selected entity"
                            type="primary"
                            icon={<ArrowRightOutlined />}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={openCurrentSubject}
                        />
                    }
                    value={query}
                />
            </div>

            {showPanel && (
                <div
                    aria-label="Entity suggestions"
                    className={`autocomplete-panel workbench-new-autocomplete-popup${
                        showFooter
                            ? " workbench-new-autocomplete-popup-with-footer"
                            : ""
                    }`}
                    id="entity-suggestions"
                    role="listbox"
                >
                    {renderPanelContent()}
                    {renderFooter()}
                </div>
            )}
        </div>
    );
};

export default WorkbenchNewSearch;
